import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useData } from "@/store/DataContext";
import EmptyState from "@/components/base/EmptyState";
import Badge from "@/components/base/Badge";
import Sheet from "@/components/base/Sheet";
import DocumentThumb from "@/components/feature/DocumentThumb";
import { formatDate } from "@/lib/format";
import type { DocumentRecord } from "@/lib/types";

const typeLabel: Record<DocumentRecord["docType"], string> = {
  factura: "Factura",
  evidencia: "Evidencia",
  comprobante: "Comprobante",
  otro: "Otro",
};

function useObjectUrl(blob: Blob | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);
  return url;
}

function downloadBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function PreviewModal({
  doc,
  onClose,
  onDelete,
}: {
  doc: DocumentRecord | null;
  onClose: () => void;
  onDelete: (id: string) => void;
}) {
  const url = useObjectUrl(doc?.blob ?? null);
  const isImage = doc?.mimeType?.startsWith("image/") ?? false;

  return (
    <Sheet open={!!doc} onClose={onClose} title={doc?.title}>
      {doc ? (
        <div className="space-y-4 pb-4">
          <div className="overflow-hidden rounded-2xl border border-background-200 bg-background-100">
            {isImage && url ? (
              <img src={url} alt={doc.title} className="max-h-[50vh] w-full object-contain" />
            ) : (
              <div className="flex h-40 flex-col items-center justify-center gap-2">
                <i className="ri-file-pdf-2-line text-4xl text-foreground-400" />
                <span className="text-[12px] text-foreground-500">{doc.fileName}</span>
              </div>
            )}
          </div>

          <div className="space-y-1.5 text-[13px]">
            <div className="flex justify-between">
              <span className="text-foreground-500">Tipo</span>
              <span className="text-foreground-900">{typeLabel[doc.docType]}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-foreground-500">Fecha</span>
              <span className="text-foreground-900">{formatDate(doc.date)}</span>
            </div>
            {doc.supplier ? (
              <div className="flex justify-between">
                <span className="text-foreground-500">Proveedor</span>
                <span className="text-foreground-900">{doc.supplier}</span>
              </div>
            ) : null}
            {doc.invoiceNumber ? (
              <div className="flex justify-between">
                <span className="text-foreground-500">N.º factura</span>
                <span className="text-foreground-900">{doc.invoiceNumber}</span>
              </div>
            ) : null}
            {doc.description ? (
              <p className="pt-1 text-[12px] leading-relaxed text-foreground-600">{doc.description}</p>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => doc.blob && downloadBlob(doc.blob, doc.fileName)}
              className="flex items-center justify-center gap-2 rounded-2xl bg-primary-500 py-3 text-[13px] font-semibold text-background-50"
            >
              <i className="ri-download-2-line text-lg" />
              Descargar
            </button>
            <button
              type="button"
              onClick={() => onDelete(doc.id)}
              className="flex items-center justify-center gap-2 rounded-2xl border border-red-200 bg-red-50 py-3 text-[13px] font-semibold text-red-600"
            >
              <i className="ri-delete-bin-line text-lg" />
              Eliminar
            </button>
          </div>
        </div>
      ) : null}
    </Sheet>
  );
}

export default function Documentos() {
  const { data, loading, deleteDocument } = useData();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<DocumentRecord | null>(null);

  useEffect(() => {
    if (params.get("nuevo") === "1") navigate("/documentos/nuevo", { replace: true });
  }, [params, navigate]);

  const docs = useMemo(() => {
    const list = [...data.documents].sort((a, b) => (a.date < b.date ? 1 : -1));
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (d) =>
        d.title.toLowerCase().includes(q) ||
        d.supplier.toLowerCase().includes(q) ||
        d.invoiceNumber.toLowerCase().includes(q),
    );
  }, [data.documents, query]);

  const handleDelete = async (id: string) => {
    await deleteDocument(id);
    setSelected(null);
  };

  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
      </div>
    );
  }

  return (
    <div className="fr-animate space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-[22px] font-bold text-foreground-950">Documentos</h1>
          <p className="mt-0.5 text-[13px] text-foreground-500">Fotos y facturas de tus operaciones</p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/documentos/nuevo")}
          aria-label="Agregar documento"
          className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 text-background-50 shadow-card"
        >
          <i className="ri-add-line text-xl" />
        </button>
      </div>

      <div className="flex items-center gap-2 rounded-2xl border border-background-200 bg-background-50 px-3.5 py-3">
        <i className="ri-search-line text-[16px] text-foreground-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nombre, proveedor o factura"
          className="w-full bg-transparent text-[14px] text-foreground-950 placeholder:text-foreground-400 focus:outline-none"
        />
        {query ? (
          <button type="button" onClick={() => setQuery("")} className="text-foreground-400">
            <i className="ri-close-circle-fill" />
          </button>
        ) : null}
      </div>

      {docs.length === 0 ? (
        <EmptyState
          icon="ri-file-text-line"
          title="Sin documentos"
          description="Toma una foto de una factura o adjunta un PDF para tenerlos siempre a mano."
        />
      ) : (
        <div className="space-y-3">
          {docs.map((doc) => (
            <button
              key={doc.id}
              type="button"
              onClick={() => setSelected(doc)}
              className="flex w-full items-center gap-3 rounded-3xl border border-background-200/70 bg-background-50 p-3 text-left shadow-card active:scale-[0.99]"
            >
              <DocumentThumb
                blob={doc.blob}
                mimeType={doc.mimeType}
                className="h-14 w-14 flex-none rounded-2xl"
                iconClass="text-2xl"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[14px] font-semibold text-foreground-950">{doc.title}</p>
                <p className="mt-0.5 truncate text-[11px] text-foreground-500">
                  {formatDate(doc.date)}
                  {doc.supplier ? ` · ${doc.supplier}` : ""}
                </p>
              </div>
              <Badge tone={doc.docType === "factura" ? "primary" : "secondary"}>
                {typeLabel[doc.docType]}
              </Badge>
            </button>
          ))}
        </div>
      )}

      <div className="flex items-start gap-2 rounded-2xl bg-secondary-50 p-3.5">
        <i className="ri-shield-check-line mt-0.5 text-secondary-600" />
        <p className="text-[12px] leading-relaxed text-secondary-900">
          Tus documentos se guardan solo en este dispositivo. Exporta una copia de seguridad
          periódicamente para no perderlos.
        </p>
      </div>

      <PreviewModal doc={selected} onClose={() => setSelected(null)} onDelete={handleDelete} />
    </div>
  );
}